/* eslint-disable no-console */
// Active safety net: re-checks for a valid MSAL account on load and (throttled) scroll, and forces logout only when truly signed out.
import { isCopEnabled } from '../../services/cookieUtils'
import { getCopTokenAuthConfig } from './config'
import { hasMsalAccount } from './msalTokenProvider'

const EXPIRY_REDIRECT_GUARD_KEY = 'cop:session-expiry-redirect-guard'
const EXPIRY_REDIRECT_GUARD_WINDOW_MS = 15000
const SCROLL_CHECK_THROTTLE_MS = 5000
const META_HOME_PATH = 'meta[name="cop-home-path"]'

let watcherInitialized = false
let watcherInitAt = 0
let lastScrollCheckAt = 0

const isAuthorMode = (): boolean => {
    try {
        const w = window as unknown as { Granite?: { author?: unknown } }
        return Boolean(w.Granite?.author)
    } catch {
        return false
    }
}

// Falls back to the current path if the home page path isn't authored.
const readHomePagePath = (): string => {
    const authored = document.querySelector(META_HOME_PATH)?.getAttribute('content')
    return authored || window.location.pathname
}

const hasRecentlyRedirectedForExpiry = (): boolean => {
    try {
        const raw = window.sessionStorage.getItem(EXPIRY_REDIRECT_GUARD_KEY)
        if (!raw) return false
        const at = Number(raw)
        return Number.isFinite(at) && Date.now() - at < EXPIRY_REDIRECT_GUARD_WINDOW_MS
    } catch {
        return false
    }
}

const markRedirectedForExpiry = (): void => {
    try {
        window.sessionStorage.setItem(EXPIRY_REDIRECT_GUARD_KEY, String(Date.now()))
    } catch {
        // Non-fatal; guard is best-effort.
    }
}

const clearTokenStorage = (debug: boolean): void => {
    try {
        window.localStorage.removeItem('cop_access_token')
    } catch (err) {
        if (debug) console.warn('[CopSessionExpiryGuard] Unable to clear localStorage token:', err)
    }
    try {
        window.sessionStorage.clear()
    } catch (err) {
        if (debug) console.warn('[CopSessionExpiryGuard] Unable to clear sessionStorage:', err)
    }
}

// Sling logout only clears the AEM session cookie, not these app cookies.
const clearCopCookies = (cookieExpiryName: string, powerPortalFlagCookieName: string, debug: boolean): void => {
    try {
        document.cookie = `${cookieExpiryName}=; Max-Age=0; path=/;`
        document.cookie = `${powerPortalFlagCookieName}=; Max-Age=0; path=/;`
    } catch (err) {
        if (debug) console.warn('[CopSessionExpiryGuard] Unable to clear cookies:', err)
    }
}

// Mirrors the existing header Logout button: /system/sling/logout.html?resource=<homePage>.
const redirectToLogout = (logoutPath: string, debug: boolean): void => {
    if (hasRecentlyRedirectedForExpiry()) return
    markRedirectedForExpiry()

    if (debug) {
        console.info('[CopSessionExpiryGuard] Token expired — logging out via "' + logoutPath + '".')
    }

    const resource = encodeURIComponent(readHomePagePath())
    const separator = logoutPath.includes('?') ? '&' : '?'
    window.location.href = `${logoutPath}${separator}resource=${resource}`
}

// treatMissingAsExpired: false on page load (token acquisition may be in flight); true on scroll after the grace period.
export const checkTokenExpiryAndLogout = async (treatMissingAsExpired = false): Promise<void> => {
    try {
        if (typeof window === 'undefined') return
        if (isAuthorMode()) return
        if (!isCopEnabled()) return

        const cfg = getCopTokenAuthConfig()

        // Authoritative check: unaffected by blocked 3rd-party cookies, unlike the old cookie-expiry check.
        const signedIn = await hasMsalAccount(cfg)
        if (signedIn) return
        if (!treatMissingAsExpired) return

        clearTokenStorage(cfg.debug)
        clearCopCookies(cfg.cookieExpiryName, cfg.powerPortalFlagCookieName, cfg.debug)
        redirectToLogout(cfg.logoutPath, cfg.debug)
    } catch (error_) {
        console.warn('[CopSessionExpiryGuard] Unexpected error while checking token expiry:', error_)
    }
}

const onScroll = (): void => {
    const now = Date.now()
    if (now - lastScrollCheckAt < SCROLL_CHECK_THROTTLE_MS) return
    lastScrollCheckAt = now

    const cfg = getCopTokenAuthConfig()
    const pastAcquisitionGracePeriod = now - watcherInitAt > cfg.iframeTimeoutMs
    checkTokenExpiryAndLogout(pastAcquisitionGracePeriod).catch(err => {
        console.warn('[CopSessionExpiryGuard] Scroll check failed:', err)
    })
}

export const initTokenExpiryWatcher = (): void => {
    if (watcherInitialized) return
    watcherInitialized = true
    watcherInitAt = Date.now()

    if (typeof window === 'undefined') return
    if (isAuthorMode() || !isCopEnabled()) return

    checkTokenExpiryAndLogout(false).catch(err => {
        console.warn('[CopSessionExpiryGuard] Initial check failed:', err)
    })
    window.addEventListener('scroll', onScroll, { passive: true })
}